/**
 * The only module that touches the DOM for the contact form.
 *
 * Separate from `view.ts` so the two features stay independently testable and a
 * change to one cannot break the other's element contract.
 */

import { CONTACT_FIELDS, type ContactField } from "./contract.js";

export type ContactValues = Record<ContactField, string>;

/** `note` is a textarea; the rest are single-line inputs. Both carry `.value`. */
export type ContactControl = HTMLInputElement | HTMLTextAreaElement;

export type ContactState =
  | { readonly kind: "idle" }
  | { readonly kind: "submitting" }
  | { readonly kind: "sent" }
  | {
      readonly kind: "error";
      readonly message: string;
      /** Field-scoped messages, shown next to the offending input. */
      readonly fieldErrors?: Readonly<Partial<Record<ContactField, string>>>;
    };

export interface ContactView {
  getValues(): ContactValues;
  clear(): void;
  render(state: ContactState): void;
  onSubmit(handler: () => void): void;
}

export type ContactElements = {
  readonly form: HTMLFormElement;
  readonly inputs: Readonly<Record<ContactField, ContactControl>>;
  readonly fieldErrors: Readonly<Record<ContactField, HTMLElement>>;
  readonly button: HTMLButtonElement;
  readonly status: HTMLElement;
};

const missing = (id: string): Error =>
  new Error(`Expected an element with id '${id}' in the document`);

function require_<T extends HTMLElement>(document: Document, id: string): T {
  const element = document.getElementById(id);
  if (element === null) throw missing(id);
  return element as T;
}

/**
 * Resolves the elements the contact form needs.
 *
 * @throws {Error} when the template does not contain them, so broken markup
 * fails loudly at startup instead of silently doing nothing.
 */
export function resolveContactElements(document: Document): ContactElements {
  const inputs = {} as Record<ContactField, ContactControl>;
  const fieldErrors = {} as Record<ContactField, HTMLElement>;

  for (const field of CONTACT_FIELDS) {
    inputs[field] = require_<ContactControl>(document, `contact-${field}`);
    fieldErrors[field] = require_(document, `contact-${field}-error`);
  }

  return {
    form: require_<HTMLFormElement>(document, "contact-form"),
    inputs,
    fieldErrors,
    button: require_<HTMLButtonElement>(document, "contact-submit"),
    status: require_(document, "contact-status"),
  };
}

export class DomContactView implements ContactView {
  constructor(private readonly elements: ContactElements) {}

  getValues(): ContactValues {
    const values = {} as ContactValues;
    for (const field of CONTACT_FIELDS) {
      values[field] = this.elements.inputs[field].value;
    }
    return values;
  }

  clear(): void {
    for (const field of CONTACT_FIELDS) {
      this.elements.inputs[field].value = "";
    }
  }

  onSubmit(handler: () => void): void {
    this.elements.form.addEventListener("submit", (event) => {
      event.preventDefault();
      handler();
    });
  }

  render(state: ContactState): void {
    const { button, status } = this.elements;

    button.disabled = state.kind === "submitting";
    this.clearFieldErrors();

    switch (state.kind) {
      case "idle":
        status.textContent = "";
        status.dataset.tone = "neutral";
        return;
      case "submitting":
        status.textContent = "Sending…";
        status.dataset.tone = "neutral";
        return;
      case "sent":
        status.textContent = "Thanks — we'll be in touch.";
        status.dataset.tone = "success";
        return;
      case "error":
        status.textContent = state.message;
        status.dataset.tone = "error";
        this.showFieldErrors(state.fieldErrors ?? {});
        return;
    }
  }

  private clearFieldErrors(): void {
    for (const field of CONTACT_FIELDS) {
      const element = this.elements.fieldErrors[field];
      element.textContent = "";
      element.hidden = true;
      this.elements.inputs[field].removeAttribute("aria-invalid");
    }
  }

  private showFieldErrors(
    errors: Readonly<Partial<Record<ContactField, string>>>,
  ): void {
    for (const field of CONTACT_FIELDS) {
      const message = errors[field];
      if (message === undefined) continue;

      const element = this.elements.fieldErrors[field];
      // textContent, never innerHTML: these strings originate server-side but
      // echo user input, so they must never be parsed as markup.
      element.textContent = message;
      element.hidden = false;
      this.elements.inputs[field].setAttribute("aria-invalid", "true");
    }
  }
}
